import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Form, Input } from "domains/3d/interfaces";
import {
  getFirstFileInput,
  getFirstPromptInput,
  getFormDefaultValue,
} from "domains/3d/utils";
import { extractFirstQueryParam } from "domains/commons/misc";
import useLastModelId, {
  DEFAULT_MODEL_IDS,
} from "domains/models/hooks/useLastModelId";
import useRouter from "domains/navigation/hooks/useRouter";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { useHandleApiError } from "infra/api/error";
import {
  GetModelsByModelIdApiResponse,
  useLazyGetAssetsByAssetIdQuery,
  useLazyGetJobIdQuery,
  useLazyGetModelsByModelIdQuery,
  usePostAssetGetBulkMutation,
} from "infra/api/generated/api";
import _ from "lodash";

interface UseImportParamsCustomInferenceArg {
  model: GetModelsByModelIdApiResponse["model"] | undefined;
  setModel: (model: GetModelsByModelIdApiResponse["model"]) => void;
  setForm: Dispatch<SetStateAction<Form>>;
  type: "3d" | "video" | "video-upscale";
}

export default function useImportParamsCustomInference({
  model,
  setModel,
  setForm,
  type,
}: UseImportParamsCustomInferenceArg) {
  const router = useRouter();
  const { selectedProject } = useTeamContext();
  const handleApiError = useHandleApiError();
  const [getModelById] = useLazyGetModelsByModelIdQuery();
  const [getAssetById] = useLazyGetAssetsByAssetIdQuery();
  const [getJobById] = useLazyGetJobIdQuery();
  const [getAssetsByIds] = usePostAssetGetBulkMutation();
  const [lastModelId] = useLastModelId(type);
  const urlParamsRef = useRef<{
    restartGeneration?: string;
    importAssetId?: string;
    modelId?: string;
    importPromptFromAssetId?: string;
  }>({});
  const [hasFailedToLoadModel, setHasFailedToLoadModel] = useState(false);

  useEffect(() => {
    if (model) {
      setHasFailedToLoadModel(false);
    }
  }, [model]);

  const urlParams = useMemo(
    () =>
      router.isReady
        ? {
            restartGeneration: extractFirstQueryParam(
              router.query.restartGeneration
            ),
            importAssetId: extractFirstQueryParam(router.query.importAssetId),
            modelId: extractFirstQueryParam(router.query.modelId),
            importPromptFromAssetId: extractFirstQueryParam(
              router.query.importPromptFromAssetId
            ),
          }
        : {},
    [
      router.isReady,
      router.query.restartGeneration,
      router.query.modelId,
      router.query.importAssetId,
      router.query.importPromptFromAssetId,
    ]
  );

  const handleSetModel = useCallback(
    (newModel: GetModelsByModelIdApiResponse["model"]) => {
      setModel(newModel);
      void router.replace(
        {
          pathname: router.pathname,
          query: {
            ..._.omit(
              router.query,
              "restartGeneration",
              "importAssetId",
              "importPromptFromAssetId"
            ),
            modelId: newModel.id,
          },
        },
        undefined,
        { shallow: true }
      );
    },
    [router, setModel]
  );

  // ----------------------------------

  const restartGeneration = useCallback(
    async (jobId: string) => {
      const { data: jobData } = await getJobById({
        projectId: selectedProject.id,
        jobId,
      });
      if (!jobData?.job) {
        return;
      }

      const jobInput = jobData?.job?.metadata?.input;

      if (!jobInput?.modelId) {
        return;
      }

      try {
        const { data: modelData } = await getModelById(
          {
            projectId: selectedProject.id,
            modelId: jobInput.modelId,
          },
          true
        );
        if (modelData?.model) {
          handleSetModel(modelData.model);
          const assetIds = (modelData.model.inputs ?? []).reduce(
            (acc, input) => {
              const value = jobInput[input.name];
              if (input.type === "file" && value && typeof value === "string") {
                acc.push(value);
              } else if (
                input.type === "file_array" &&
                value &&
                Array.isArray(value) &&
                value.every(
                  (x: string) => x !== undefined && typeof x === "string"
                )
              ) {
                acc.push(...value);
              }
              return acc;
            },
            [] as string[]
          );
          const assets = await getAssetsByIds({
            projectId: selectedProject.id,
            body: {
              assetIds,
            },
          }).unwrap();

          const assetsByIds = _.keyBy(assets.assets, "id");

          setForm(
            (modelData.model.inputs ?? []).reduce((acc, input) => {
              if (input.type === "file") {
                const asset = assetsByIds[jobInput[input.name]];
                acc[input.name] = asset;
              } else if (input.type === "file_array") {
                const assetIds: string[] | undefined = jobInput[input.name];
                acc[input.name] =
                  assetIds
                    ?.map((assetId) => assetsByIds[assetId])
                    .filter((v) => v !== undefined) ?? [];
              } else {
                acc[input.name] =
                  jobInput[input.name] ?? getFormDefaultValue(input);
              }
              return acc;
            }, {} as Form)
          );
        }
      } catch (error) {
        setHasFailedToLoadModel(true);
        void router.replace(
          {
            pathname: router.pathname,
            query: _.omit(router.query, "restartGeneration"),
          },
          undefined,
          { shallow: true }
        );
        handleApiError(error, "There was an error loading the parameters");
      }
    },
    [
      getAssetsByIds,
      getJobById,
      getModelById,
      handleApiError,
      handleSetModel,
      selectedProject.id,
      setForm,
      router,
    ]
  );

  // ----------------------------------

  const getValidModel = useCallback(
    async (
      validateModel: (
        model: GetModelsByModelIdApiResponse["model"] | undefined
      ) => Input | undefined
    ) => {
      if (validateModel(model)) {
        return model;
      }

      try {
        if (router.query.modelId && typeof router.query.modelId === "string") {
          const { data: modelData } = await getModelById(
            { projectId: selectedProject.id, modelId: router.query.modelId },
            true
          );
          if (validateModel(modelData?.model)) {
            return modelData?.model;
          }
        }

        if (lastModelId) {
          const { data: modelData } = await getModelById(
            { projectId: selectedProject.id, modelId: lastModelId },
            true
          );
          if (validateModel(modelData?.model)) {
            return modelData?.model;
          }
        }

        const { data: modelData } = await getModelById(
          { projectId: selectedProject.id, modelId: DEFAULT_MODEL_IDS[type] },
          true
        );
        return modelData?.model;
      } catch (_) {}

      return undefined;
    },
    [
      model,
      router.query.modelId,
      selectedProject.id,
      getModelById,
      type,
      lastModelId,
    ]
  );

  const importAsset = useCallback(
    async (assetId: string) => {
      try {
        const [asset, newModel] = await Promise.all([
          getAssetById(
            {
              projectId: selectedProject.id,
              assetId,
            },
            true
          ).unwrap(),
          getValidModel(getFirstFileInput),
        ]);

        if (!newModel) {
          setHasFailedToLoadModel(true);
          return;
        }
        handleSetModel(newModel);

        if (!asset?.asset) {
          return;
        }

        setTimeout(() => {
          const firstFileInput = getFirstFileInput(newModel);
          if (firstFileInput) {
            setForm((form) => ({
              ...form,
              [firstFileInput.name]:
                firstFileInput.type === "file" ? asset.asset : [asset.asset],
            }));
          }
        }, 100);
      } catch (error) {
        void router.replace(
          {
            pathname: router.pathname,
            query: _.omit(router.query, "importAssetId"),
          },
          undefined,
          { shallow: true }
        );
        handleApiError(error, "There was an error importing the asset");
      }
    },
    [
      getAssetById,
      handleApiError,
      handleSetModel,
      selectedProject.id,
      setForm,
      router,
      getValidModel,
    ]
  );

  // ----------------------------------

  const importPromptFromAsset = useCallback(
    async (assetId: string) => {
      try {
        const [asset, newModel] = await Promise.all([
          getAssetById(
            {
              projectId: selectedProject.id,
              assetId,
            },
            true
          ).unwrap(),
          getValidModel(getFirstPromptInput),
        ]);

        if (!newModel) {
          setHasFailedToLoadModel(true);
          return;
        }
        handleSetModel(newModel);

        if (!asset?.asset) {
          return;
        }

        setTimeout(() => {
          const firstPromptInput = getFirstPromptInput(newModel);
          if (firstPromptInput) {
            setForm((form) => ({
              ...form,
              [firstPromptInput.name]: asset.asset.metadata.prompt,
            }));
          }
        }, 100);
      } catch (error) {
        void router.replace(
          {
            pathname: router.pathname,
            query: _.omit(router.query, "importPromptFromAssetId"),
          },
          undefined,
          { shallow: true }
        );
        handleApiError(error, "There was an error importing the prompt");
      }
    },
    [
      getAssetById,
      handleApiError,
      handleSetModel,
      selectedProject.id,
      setForm,
      router,
      getValidModel,
    ]
  );

  // ----------------------------------

  useEffect(() => {
    if (_.isEqual(urlParams, urlParamsRef.current)) {
      return;
    }

    const previousUrlParams = { ...urlParamsRef.current };
    urlParamsRef.current = { ...urlParams };

    if (urlParams.restartGeneration) {
      void restartGeneration(urlParams.restartGeneration);
    } else if (urlParams.importAssetId) {
      void importAsset(urlParams.importAssetId);
    } else if (urlParams.importPromptFromAssetId) {
      void importPromptFromAsset(urlParams.importPromptFromAssetId);
    } else if (
      urlParams.modelId &&
      urlParams.modelId !== previousUrlParams.modelId
    ) {
      void getModelById(
        {
          projectId: selectedProject.id,
          modelId: urlParams.modelId,
        },
        true
      ).then(({ data: modelData }) => {
        if (modelData?.model) {
          handleSetModel(modelData.model);
        } else {
          setHasFailedToLoadModel(true);
        }
      });
    } else if (!urlParams.modelId) {
      void getModelById(
        {
          projectId: selectedProject.id,
          modelId: lastModelId,
        },
        true
      ).then(({ data: modelData }) => {
        if (modelData?.model) {
          handleSetModel(modelData.model);
        } else {
          setHasFailedToLoadModel(true);
        }
      });
    }
  }, [
    getModelById,
    handleSetModel,
    importAsset,
    lastModelId,
    restartGeneration,
    selectedProject.id,
    urlParams,
    importPromptFromAsset,
  ]);

  return {
    hasFailedToLoadModel,
  };
}
